import { useApi } from "@platform/api";
import { useNotification } from "@platform/hooks";
import { useMutation } from "@tanstack/react-query";

const TEMPLATE_FILENAME = "Census template.xlsx";

const saveFile = (file: Blob, filename: string): void => {
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * Downloads the blank census workbook. It goes through the API client rather
 * than a plain link because the request has to carry the user's token.
 */
export const useCensusTemplateDownload = (): {
  readonly download: () => void;
  readonly isDownloading: boolean;
} => {
  const axiosInstance = useApi();
  const { showError } = useNotification();

  const mutation = useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.get<Blob>("/bp/v1/census-template", {
        responseType: "blob",
      });

      return response.data;
    },
    onSuccess: (file) => saveFile(file, TEMPLATE_FILENAME),
    onError: () => showError("The census template could not be downloaded."),
  });

  return {
    download: () => mutation.mutate(),
    isDownloading: mutation.isPending,
  };
};
